import React from 'react';
import { get } from 'lodash';
import moment from 'moment';
import Tags from 'components/community/parts/tags/Tags';
import { CEmploymentType } from 'constants/community.const';
import styled from 'styled-components';
import { device, SecondaryColor } from 'GlobalStyles';

type TType = {
  type?: string;
};

type TPercent = {
  percent?: number;
};

type TInterviewDetailProps = {
  board?: string;
  header?: { title: string; employment_type: string; interview_date: string };
  data: {
    interview_progress: string;
    interview_atmosphere: string;
    interview_level: string;
    interview_pass: string;
    fit_importance: string;
    case_importance: string;
    questions: Array<string>;
  };
  problemSolving?: string;
};

const InterviewDetail = ({ board, header, data, problemSolving }: TInterviewDetailProps) => {
  const interviewYear = moment(get(header, 'interview_date')).format('YYYY');
  const interviewMonth = moment(get(header, 'interview_date')).format('M');
  return (
    <>
      {data && (
        <StyledContainer type={header?.title}>
          {header && (
            <div>
              <h2 className='company'>
                {header.title === 'McKinsey' ||
                header.title === 'BCG' ||
                header.title === 'Bain & Company' ? (
                  <span>McKinsey&middot;BCG&middot;Bain</span>
                ) : (
                  header.title
                )}
              </h2>
              <div className='subtitles'>
                <h3 className='employmentType'>{CEmploymentType[header.employment_type]}</h3>
                <h3 className='employmentDate'>{`${interviewYear}년 ${interviewMonth}월`}</h3>
              </div>
            </div>
          )}

          <StyledInterviewDetail type={header?.title}>
            <StyledInfos>
              <div className='info'>
                <div className='label'>인터뷰 분위기</div>
                <Tags type={data.interview_atmosphere} />
              </div>
              <div className='info'>
                <div className='label'>인터뷰 난이도</div>
                <Tags type={data.interview_level} />
              </div>
              <div className='info'>
                <div className='label'>인터뷰 방식</div>
                <Tags type={data.interview_progress} />
              </div>
              <div className='info'>
                <div className='label'>인터뷰 결과</div>
                <Tags type={data.interview_pass} />
              </div>
            </StyledInfos>
            {data.fit_importance && (
              <StyledInfoGaugeBar percent={Number(data.fit_importance)}>
                <div className='label'>인터뷰 유형 비중</div>
                <div className='gauge'>
                  <div className='percentage' />
                  <div className='gaugeLabel'>
                    <div>{`Fit ${Number(data.fit_importance)}%`}</div>
                    <div>{`Case ${Number(data.case_importance)}%`}</div>
                  </div>
                </div>
              </StyledInfoGaugeBar>
            )}
          </StyledInterviewDetail>

          <StyledInterviewQuestions>
            <h2 className='card_section_title'>인터뷰 질문</h2>
            <div className='questions first'>
              <div className='icon'>Q1.</div>
              <div className='question'>{data.questions[0]}</div>
            </div>
            <div className='questions second'>
              <div className='icon'>Q2.</div>
              <div className='question'>{data.questions[1]}</div>
            </div>
          </StyledInterviewQuestions>

          {problemSolving && (
            <StyledInterviewQuestions type='final'>
              <h2 className='card_section_title second'>문제 풀이 접근법</h2>
              <div className='questions problemSolving'>
                <div className='question'>{problemSolving}</div>
              </div>
            </StyledInterviewQuestions>
          )}
        </StyledContainer>
      )}
    </>
  );
};

export default InterviewDetail;

const StyledContainer = styled.article<TType>`
  width: 100%;
  padding: ${({ type }) => (!type ? '0' : '25px 28px')};
  border: ${({ type }) => type && '1px solid #e4e4e4'};
  border-radius: 10px;

  .company {
    font-size: 28px;
    font-weight: 900;
    color: #14112a;
  }

  .subtitles {
    display: flex;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 18px;
      line-height: 1;
      color: #14112a;

      @media screen and ${device.lg} {
        font-size: 14px;
      }
    }
  }

  .employmentType {
    padding-right: 10px;
    border-right: 1px solid #aeaeae;
  }

  .employmentDate {
    padding-left: 10px;
  }
`;

const StyledInterviewDetail = styled.div<TType>`
  width: ${({ type }) => (!type ? '100%' : '95%')};
  margin: ${({ type }) => (!type ? '0' : '25px auto 0 auto;')};

  @media screen and ${device.lg} {
    margin-left: 0;
  }
`;

const StyledInfos = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and ${device.lg} {
    flex-direction: column;
  }

  .info {
    display: flex;
    align-items: center;

    @media screen and ${device.lg} {
      margin: 6px 0;
    }

    .label {
      margin-right: 13px;
      color: #aeaeae;
    }
  }
`;

const StyledInfoGaugeBar = styled.div<TPercent>`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;

  .label {
    width: 130px;
    margin-right: 10px;
    color: #aeaeae;
  }

  .gauge {
    position: relative;
    width: 100%;
    height: 9px;
    background-color: #e4e4e4;

    .percentage {
      position: absolute;
      width: ${({ percent }) => `${percent}%`};
      height: 9px;
      top: 0;
      left: 0;
      background-color: #bdbdbd;
    }
  }

  .gaugeLabel {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    color: #14112a;
  }
`;

const StyledInterviewQuestions = styled.div<TType>`
  margin-top: 25px;

  .card_section_title {
    margin: 5px 0;
    font-size: initial;
    color: ${SecondaryColor};
  }

  .icon {
    margin-right: 15px;
    min-width: 30px;
    font-size: 18px;
    font-weight: 900;
    color: ${SecondaryColor};
  }

  .questions {
    display: flex;
    padding: 25px 20px;
    background-color: #f9f9f9;
  }

  .questions.first {
    padding-bottom: 0;
  }

  .questions.second {
    padding-top: 20px;
  }

  .question {
    font-size: 18px;

    @media screen and ${device.lg} {
      font-size: 16px;
    }
  }

  .problemSolving {
    margin-bottom: 50px;
  }
`;
